import { CLASS_ACTIVE, CLASS_HIDDEN, CLASS_VISIBLE } from "../tools/constants/classes";
import { throttle } from "../tools/utilities/event-tools";

const CLASS_ANIMATING = 'is-animating'

export class Tabs extends HTMLElement {
  private tabsWrapper: HTMLDivElement | null;
  private tabsElement: NodeListOf<HTMLButtonElement>;
  private tabActiveIndicator: HTMLDivElement | null;
  private tabActiveIndex: number = 0;
  private firstTabElement: HTMLButtonElement | null;

  private tabClickCallback: ((...args: any[]) => void) | null;
  private throttledResize: Function;

  constructor() {
    super();

    this.tabsWrapper = this.querySelector<HTMLDivElement>('[data-tab-wrapper]');
    this.tabsElement = this.querySelectorAll<HTMLButtonElement>('[data-tab]');
    this.tabActiveIndicator = this.querySelector<HTMLDivElement>('[data-tab-indicator]');

    this.firstTabElement = this.tabsElement[0];

    this.tabClickCallback = null;
    this.throttledResize = throttle(this.handleResize.bind(this), 200);
  }

  connectedCallback() {
    if (this.tabActiveIndicator !== null && this.tabsElement.length > 0) {
      this.initializeTabs();

      window.addEventListener('resize', this.throttledResize as EventListener);

      this.onTabClick({ buttonElement: this.firstTabElement, index: 0 });
    }
  }

  private initializeTabs() {
    this.addClickEventListeners();
  }

  private addClickEventListeners() {
    Array.from(this.tabsElement).forEach((element, index) => {
      element.addEventListener('click', () => this.onTabClick({ buttonElement: element, index }));
    });
  }

  setCallback(callback: (...args: any[]) => void): void {
    this.tabClickCallback = callback;
  }

  private onTabClick({
    buttonElement,
    index
  }: {
    buttonElement: HTMLButtonElement | null;
    index?: number;
  }): void {

    if (index !== undefined) {
      this.tabActiveIndex = index;
    }

    if (buttonElement !== null) {
      this.updateIndicatorStyles(buttonElement);
    }

    if (index !== undefined && this.tabClickCallback !== null) {
      this.tabClickCallback(index);
    }

  }

  private handleResize = () => {
    const activeButton = this.tabsElement[this.tabActiveIndex];

    if (activeButton) {
      this.updateIndicatorStyles(activeButton);
    }
  }

  private updateIndicatorStyles(buttonElement: HTMLButtonElement): void {
    if (this.tabActiveIndicator !== null && this.tabsWrapper !== null) {
      this.tabsElement.forEach(el => {
        el.classList.remove(CLASS_ACTIVE);
      })

      const elementRect = buttonElement.getBoundingClientRect();
      const parentRect = this.tabsWrapper.getBoundingClientRect();
      const width = elementRect.width;
      const distanceFromLeft = elementRect.left - parentRect.left - parseFloat(getComputedStyle(this.tabsWrapper).paddingLeft);

      this.classList.add(CLASS_ANIMATING);
      this.tabActiveIndicator.classList.add(CLASS_VISIBLE);
      this.tabActiveIndicator.style.width = `${width}px`;

      this.tabActiveIndicator.style.transform = `translateX(${distanceFromLeft}px)`;

      setTimeout(() => {
        if (this.tabActiveIndicator !== null) {
          this.classList.remove(CLASS_ANIMATING);
          buttonElement.classList.add(CLASS_ACTIVE);
          this.tabActiveIndicator.classList.remove(CLASS_VISIBLE);
        }
      }, 600);

      this.parentElement?.classList.add('has-shadow');
    }
  }
}

if (!customElements.get('tabs-object')) {
  customElements.define('tabs-object', Tabs);
}